import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { resolvePerPage } from "utils/helpers";

const useReportPage = (config) => {
    const {
        // ззовні:
        storageKeys,              // { perPage, columns, filters }
        limits,                   // { minPerPage, maxPerPage }
        columnsAll,               // масив колонок сторінки (із props)
        defaultColumnsKeys,       // ["range","a_number",...]
        buildDefaultFilter,       // (service, dateTimeState, savedFilter) => filter
        validateFilter,           // (filter) => booleanHasError
        adaptFilterByService,     // (service, filter) => nextFilter
        composeSummaryRow,        // (dataRowTemplate, summaries) => summaryRow
        fetchList,                // (service, perPage, filter, page) => void
        exportReport,             // (service, filter, page, perPage) => void
        visibleKeysForService,
        toUTC,
        service,
        type,
        perPageProp,
        dateTimeState,
        dataList,                 // trafficStat | cdr_list
        summaries,
        calcActiveFilters
    } = config;

    const savedTableFilterRef = useRef(
        (() => {
            const raw = localStorage.getItem(storageKeys.filters);
            if (raw) localStorage.removeItem(storageKeys.filters);
            try { return JSON.parse(raw) || null; } catch { return null; }
        })()
    );

    const getInitialPerPage = useCallback(() => resolvePerPage(
        storageKeys.perPage,
        perPageProp,
        limits.minPerPage
    ), [limits.minPerPage, perPageProp, storageKeys.perPage]);

    const [perPage, setPerPage] = useState(getInitialPerPage);

    const readSavedColumns = useCallback(() => {
        try {
            const value = JSON.parse(localStorage.getItem(storageKeys.columns));

            return Array.isArray(value) ? value : null;
        } catch {
            return null;
        }
    }, [storageKeys.columns]);

    const [savedTableColumns, setSavedTableColumns] = useState(readSavedColumns);

    const [filter, setFilter] = useState(
        buildDefaultFilter(service, dateTimeState, savedTableFilterRef.current)
    );

    const [shownColumns, setShownColumns] = useState([]);
    const [activeFiltersCount, setActiveFiltersCount] = useState(0);
    const [showFilters, setShowFilters] = useState(false);

    const curPerPageRef = useRef(null);
    const prevServiceRef = useRef(service);
    const isServiceReady = service === true || service === false;

    const applyColumnsByIds = useCallback((ids, persist = true) => {
        if (!Array.isArray(ids)) {
            return false;
        }

        const validIds = Array.isArray(ids)
            ? ids.filter((id) => columnsAll.some((column) => column.id === id))
            : [];

        if (ids.length && !validIds.length) {
            return false;
        }

        const next = columnsAll.filter((column) => validIds.includes(column.id));

        if (persist) {
            localStorage.setItem(storageKeys.columns, JSON.stringify(validIds));
        }

        setShownColumns(next);
        setSavedTableColumns(validIds);

        return true;
    }, [columnsAll, storageKeys.columns]);

    const setColumns = useCallback((visibleKeys, persist = false) => {
        const next = columnsAll.filter(c =>
            !defaultColumnsKeys.includes(c.dataKey) ||
            visibleKeys.includes(c.dataKey)
        );
        const columnsIdList = next.map((c) => c.id);

        if (persist) {
            localStorage.setItem(storageKeys.columns, JSON.stringify(columnsIdList));
        }

        setShownColumns(next);
        setSavedTableColumns(columnsIdList)
    }, [columnsAll, defaultColumnsKeys, storageKeys.columns]);

    const onSaveColumnsTable = useCallback((ids) => {
        applyColumnsByIds(ids);
    }, [applyColumnsByIds]);

    const toValidDate = useCallback((value) => {
        if (value instanceof Date) {
            return Number.isNaN(value.getTime()) ? null : value;
        }

        const date = new Date(value);
        return Number.isNaN(date.getTime()) ? null : date;
    }, []);

    const normalizeDates = useCallback((f) => {
        const n = { ...f };
        if (n.range?.length) {
            const startDate = toValidDate(n.range[0]);
            const endDate = toValidDate(n.range[1]);

            if (startDate && endDate) {
                n.start_date = toUTC(startDate);
                n.end_date = toUTC(endDate);
                n.range = [startDate, endDate];
            }
        }
        return n;
    }, [toUTC, toValidDate]);

    const clampAndPersistPerPage = useCallback((val) => {
        const n = Number(val) || curPerPageRef.current || perPage;
        const clamped = Math.max(limits.minPerPage, Math.min(limits.maxPerPage, n));
        curPerPageRef.current = clamped;
        localStorage.setItem(storageKeys.perPage, JSON.stringify(clamped));
        return clamped;
    }, [limits, perPage, storageKeys.perPage]);

    const applyFilterAndFetch = useCallback((f, page = 1, forcedPerPage = null) => {
        if (!isServiceReady) return;

        const prepared = normalizeDates(f);
        if (!validateFilter(prepared)) {
            fetchList(service,
                forcedPerPage || curPerPageRef.current || perPage,
                prepared,
                page
            );
        }
    }, [fetchList, isServiceReady, normalizeDates, perPage, service, validateFilter]);

    const onChangePerPage = useCallback((val) => {
        const nextPerPage = clampAndPersistPerPage(val);
        setPerPage(nextPerPage);
        applyFilterAndFetch(filter, 1, nextPerPage);
    }, [applyFilterAndFetch, clampAndPersistPerPage, filter]);

    const onChangePage = useCallback((page) => {
        applyFilterAndFetch(filter, page);
    }, [applyFilterAndFetch, filter]);

    const getItems = useCallback((page = 1, nextPerPage = perPage) => {
        const resolvedPerPage = clampAndPersistPerPage(nextPerPage);

        setPerPage(resolvedPerPage);
        applyFilterAndFetch(filter, page, resolvedPerPage);
    }, [applyFilterAndFetch, clampAndPersistPerPage, filter, perPage]);

    const onChangeFilters = useCallback((f) => {
        setFilter(f);
        setActiveFiltersCount(calcActiveFilters(f));
    }, [calcActiveFilters]);

    const exportNow = useCallback((page) => {
        const prepared = normalizeDates(filter);
        exportReport(service, prepared, page, curPerPageRef.current || perPageProp);
    }, [exportReport, filter, normalizeDates, perPageProp, service]);

    // mount
    useEffect(() => {
        if (!isServiceReady) return;

        curPerPageRef.current = perPage;
        // підбери стартові колонки для конкретної сторінки
        const hasSavedColumns = applyColumnsByIds(readSavedColumns(), false);

        if (!hasSavedColumns) {
            setColumns(visibleKeysForService(service));
        }
        applyFilterAndFetch(filter, 1, perPage);
        setActiveFiltersCount(calcActiveFilters(filter));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // react to service/type
    useEffect(() => {
        if (!isServiceReady) return;

        if (service !== prevServiceRef.current) {
            const nextFilter = adaptFilterByService(service, filter);
            const nextPerPage = getInitialPerPage();

            setFilter(nextFilter);
            setPerPage(nextPerPage);
            curPerPageRef.current = nextPerPage;
            setColumns(visibleKeysForService(service));
            applyFilterAndFetch(nextFilter, 1, nextPerPage);
            prevServiceRef.current = service;
        }
    }, [adaptFilterByService, applyFilterAndFetch, filter, getInitialPerPage, isServiceReady, service, setColumns, visibleKeysForService]);

    // summaries
    const { dataWithSummaries, summariesRow } = useMemo(() => {
        if (!summaries || !(dataList || []).length) return { dataWithSummaries: dataList, summariesRow: null };
        const row = composeSummaryRow(dataList[0], summaries);
        return { dataWithSummaries: [...dataList, row], summariesRow: row };
    }, [composeSummaryRow, dataList, summaries]);

    return {
        state: {
            filter, shownColumns, perPage, savedTableColumns,
            activeFiltersCount, showFilters, dataWithSummaries, summariesRow
        },
        refs: { curPerPageRef },
        actions: {
            setShowFilters, onChangeFilters, onChangePerPage, onChangePage,
            setColumns, applyFilterAndFetch, exportNow, getItems, onSaveColumnsTable
        }
    };
};

export default useReportPage;
